import { ChangeEvent, useEffect, useRef, useState } from "react";

import { useMetering } from "api/services/useMetering";
import { MONTH_TO_DATE, USAGE_MAPPINGS, UsageMapping, YEAR_TO_DATE } from "config/copies/usage";
import {
  isAccountMetricsValid,
  isDetectorsMetricsValid,
  parseMeteringToBarchartInfo,
  sumDetectorInformation,
} from "utils/meteringUtils";

const dayjs = require("dayjs");
const utc = require("dayjs/plugin/utc");

dayjs.extend(utc);

const DEFAULT_USAGE_PERIOD = MONTH_TO_DATE;

const currentYear = dayjs().year();
const currentMonth = dayjs().month() + 1;

export const useMeteringInfo = () => {
  const [period, setPeriod] = useState<UsageMapping>(DEFAULT_USAGE_PERIOD);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [firstOfSelectedMonth, setFirstOfSelectedMonth] = useState("");
  const [endDate, setEndDate] = useState("");
  const interval = useRef("day");

  // Format the date in UTC and then slice off the timezone part
  const formattedMonth = selectedMonth.toString().padStart(2, "0"); // Ensure month is in 'MM' format
  const dateStr = `${selectedYear}-${formattedMonth}-01T00:00:00`; // Format: YYYY-MM-DDTHH:mm:ss

  useEffect(() => {
    if (period === MONTH_TO_DATE) {
      interval.current = "day";
      setFirstOfSelectedMonth(dayjs.utc(dateStr).startOf("month").format("YYYY-MM-DDTHH:mm:ss"));
      setEndDate(dayjs.utc(dateStr).endOf("month").format("YYYY-MM-DDTHH:mm:ss"));
    } else if (period === YEAR_TO_DATE) {
      interval.current = "month";
      setFirstOfSelectedMonth(dayjs.utc(dateStr).startOf("year").format("YYYY-MM-DDTHH:mm:ss"));
      setEndDate(dayjs.utc(dateStr).endOf("year").format("YYYY-MM-DDTHH:mm:ss"));
    }
  }, [dateStr, period]);

  const {
    accountMetrics,
    isLoadingAccountMetrics: loadingAccountMetrics,
    accountMetricsError,
    detectorMetrics: detectorsMetrics,
    isLoadingDetectorMetrics: loadingDetectorMetrics,
  } = useMetering({
    accountParams: {
      interval: interval.current,
      start: firstOfSelectedMonth,
      end: endDate,
    },
    detectorParams: {
      interval: "forever",
      start: firstOfSelectedMonth,
      end: endDate,
    },
    options: {
      enabled: !!firstOfSelectedMonth,
    },
  });

  const handleTimePeriodChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      target: { value },
    } = event;

    const selectedPeriod = USAGE_MAPPINGS.find((usageMapping) => usageMapping.backend === value);
    setPeriod(selectedPeriod || DEFAULT_USAGE_PERIOD);
  };

  // We parse the data from the API into a format that the MeteringBarChart can use
  const metrics = sumDetectorInformation(isDetectorsMetricsValid(detectorsMetrics) ? detectorsMetrics : []);
  const meteringBarchartdata = parseMeteringToBarchartInfo(
    interval.current,
    isAccountMetricsValid(accountMetrics) ? accountMetrics : []
  );

  const handleMonthChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSelectedMonth(event.target.value as unknown as number);
  };

  const handleYearChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newSelectedYear = event.target.value as unknown as number;
    setSelectedYear(newSelectedYear);

    // Check if the selected year is the current year and if the selected month is in the future
    if (newSelectedYear === currentYear && selectedMonth > currentMonth) {
      setSelectedMonth(currentMonth);
    }
  };

  return {
    accountMetricsError,
    detectorsMetrics,
    handleMonthChange,
    handleTimePeriodChange,
    handleYearChange,
    loadingAccountMetrics,
    loadingDetectorMetrics,
    meteringBarchartdata,
    metrics,
    period,
    selectedMonth,
    selectedYear,
  };
};
